import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Shield, FileText, Scale, CheckCircle2, AlertCircle } from 'lucide-react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { supabase } from '../../lib/supabase';

export default function TermsOfServicePage() {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState('');

  useEffect(() => {
    fetchTermsOfService();
  }, []);

  const fetchTermsOfService = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase?.from('legal_documents')?.select('*')?.eq('document_type', 'terms_of_service')?.eq('is_active', true)?.single();

      if (fetchError) throw fetchError;

      if (data) {
        setContent(data?.content || '');
        setLastUpdated(new Date(data?.updated_at)?.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }));
      }
    } catch (err) {
      console.error('Error fetching terms of service:', err);
      setError('Unable to load terms of service. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = (markdown) => {
    if (!markdown) return null;

    const lines = markdown?.split('\n');
    const elements = [];

    lines?.forEach((line, index) => {
      // H1 headers
      if (line?.startsWith('# ')) {
        elements?.push(
          <h1 key={index} className="text-4xl font-black mb-6 mt-8 text-gray-900" style={{ fontFamily: 'Roboto Slab, serif' }}>
            {line?.replace('# ', '')}
          </h1>
        );
      }
      // H2 headers
      else if (line?.startsWith('## ')) {
        elements?.push(
          <h2 key={index} className="text-3xl font-bold mb-4 mt-8 text-blue-900 border-b-2 border-blue-200 pb-2" style={{ fontFamily: 'Roboto Slab, serif' }}>
            {line?.replace('## ', '')}
          </h2>
        );
      }
      // H3 headers
      else if (line?.startsWith('### ')) {
        elements?.push(
          <h3 key={index} className="text-2xl font-bold mb-3 mt-6 text-blue-800" style={{ fontFamily: 'Roboto Slab, serif' }}>
            {line?.replace('### ', '')}
          </h3>
        );
      }
      // Bold text
      else if (line?.startsWith('**') && line?.endsWith('**')) {
        elements?.push(
          <p key={index} className="font-bold mb-3 text-gray-900" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            {line?.replace(/\*\*/g, '')}
          </p>
        );
      }
      // List items
      else if (line?.startsWith('- ')) {
        elements?.push(
          <li key={index} className="ml-6 mb-2 text-gray-700 leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            {line?.replace('- ', '')}
          </li>
        );
      }
      // Horizontal rule
      else if (line?.startsWith('---')) {
        elements?.push(<hr key={index} className="my-8 border-gray-300" />);
      }
      // Regular paragraphs
      else if (line?.trim() !== '') {
        elements?.push(
          <p key={index} className="mb-4 text-gray-700 leading-relaxed text-lg" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            {line}
          </p>
        );
      }
    });

    return elements;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Helmet>
        <title>Terms of Service | Sailors Bail Bonds - Legal Agreement & Conditions</title>
        <meta name="description" content="Complete Terms of Service for Sailors Bail Bonds. Read our legal agreement, client obligations, service conditions, and ADA compliance policies for bail bond services in California." />
        <meta name="keywords" content="bail bond terms, service agreement, legal terms, client obligations, California bail bonds, ADA compliance" />
        <link rel="canonical" href="https://sailorsbailbonds.com/terms-of-service" />
      </Helmet>

      <Header />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="bg-blue-600 p-4 rounded-full">
              <FileText className="w-12 h-12 text-white" />
            </div>
          </div>
          <h1 className="text-5xl font-black mb-4 text-gray-900" style={{ fontFamily: 'Roboto Slab, serif' }}>
            Terms of Service
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Please read these terms carefully before using our bail bond services
          </p>
          {lastUpdated && (
            <p className="text-sm text-gray-500 mt-4" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              Last Updated: {lastUpdated}
            </p>
          )}
        </div>

        {/* Key Highlights */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-8 mb-12">
          <div className="flex items-center mb-4">
            <Shield className="w-6 h-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-blue-900" style={{ fontFamily: 'Roboto Slab, serif' }}>
              Key Points
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="font-bold text-gray-900 mb-1" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  ADA Compliant Services
                </p>
                <p className="text-gray-700 text-sm" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Full accessibility for individuals with disabilities
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="font-bold text-gray-900 mb-1" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  California Licensed
                </p>
                <p className="text-gray-700 text-sm" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Regulated by California Department of Insurance
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="font-bold text-gray-900 mb-1" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Federal Compliance
                </p>
                <p className="text-gray-700 text-sm" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Adheres to FCRA, GLBA, TCPA regulations
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="font-bold text-gray-900 mb-1" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  24/7 Emergency Service
                </p>
                <p className="text-gray-700 text-sm" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Available any time at (916) 256-3468
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content Loading States */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <p className="text-red-800 font-bold mb-2" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              {error}
            </p>
            <button
              onClick={fetchTermsOfService}
              className="mt-4 bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors font-semibold"
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              Try Again
            </button>
          </div>
        )}

        {/* Main Content */}
        {!loading && !error && content && (
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 border-2 border-gray-200">
            <div className="prose prose-lg max-w-none">
              {renderContent(content)}
            </div>
          </div>
        )}

        {/* Contact Section */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-white text-center">
          <Scale className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-black mb-4" style={{ fontFamily: 'Roboto Slab, serif' }}>
            Questions About Our Terms?
          </h2>
          <p className="text-blue-100 text-lg mb-6 max-w-2xl mx-auto" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Our team is available 24/7 to answer any questions about our Terms of Service
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:+19162563468"
              className="bg-green-500 text-white px-8 py-4 rounded-xl font-black text-lg hover:bg-green-600 transition-all transform hover:scale-105 shadow-xl"
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              Call (916) 256-3468
            </a>
            <Link
              to="/contact-us"
              className="bg-white text-blue-600 px-8 py-4 rounded-xl font-black text-lg hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl"
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              Contact Us
            </Link>
          </div>
        </div>

        {/* Related Links */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Related Legal Documents
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/privacy-policy"
              className="text-blue-600 hover:text-blue-800 font-semibold underline"
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              Privacy Policy
            </Link>
            <Link
              to="/legal-disclaimer-and-compliance"
              className="text-blue-600 hover:text-blue-800 font-semibold underline"
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              Legal Disclaimer
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}